import React, { useState, useEffect } from 'react';
import { 
  BarChart3, TrendingUp, TrendingDown, Loader2, Calendar,
  Package, Truck, Users, AlertCircle, CheckCircle, DollarSign,
  MapPin, RefreshCw, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/services/api';

const AnalyticsPanel = () => {
  const [summary, setSummary] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [areaData, setAreaData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setIsLoading(true);
    try {
      const [summaryRes, dailyRes, areaRes] = await Promise.all([
        api.get(`/api/admin/analytics/summary?period=${period}`),
        api.get(`/api/admin/analytics/daily?days=${period === '7d' ? 7 : period === '30d' ? 30 : 90}`),
        api.get('/api/admin/analytics/areas')
      ]);

      if (summaryRes.data.success) {
        setSummary(summaryRes.data.summary);
      }
      if (dailyRes.data.success) {
        setDailyData(dailyRes.data.data);
      }
      if (areaRes.data.success) {
        setAreaData(areaRes.data.areas);
      }
    } catch (error) {

      toast.error('Failed to load analytics');
      // Set default values
      setSummary({
        total_orders: 0,
        delivery_orders: 0,
        minimove_orders: 0,
        orders_completed: 0,
        orders_cancelled: 0,
        order_growth_percent: 0,
        previous_period_orders: 0,
        fulfillment_rate: 0,
        new_customers: 0,
        complaints: 0,
        complaint_rate: 0,
        referral_signups: 0,
        referral_uses: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatGrowth = (value) => {
    if (value > 0) return `+${value}%`;
    return `${value}%`;
  };

  // Simple chart component using CSS
  const SimpleBarChart = ({ data, maxHeight = 150 }) => {
    if (!data || data.length === 0) return null;
    
    const maxValue = Math.max(...data.map(d => d.total_orders), 1);
    
    return (
      <div className="flex items-end gap-1 h-[150px]">
        {data.map((item, index) => (
          <div 
            key={item.date || `bar-${index}`}
            className="flex-1 flex flex-col items-center group"
          >
            <div 
              className="w-full bg-[#039fff] rounded-t hover:bg-[#fb6402] transition-colors relative"
              style={{ 
                height: `${(item.total_orders / maxValue) * maxHeight}px`,
                minHeight: item.total_orders > 0 ? '4px' : '0'
              }}
            >
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {item.total_orders} orders
              </div>
            </div>
            {data.length <= 14 && (
              <span className="text-[10px] text-gray-400 mt-1 rotate-45 origin-left">
                {item.date.slice(5)}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#039fff]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#001A47]">Analytics Dashboard</h2>
          <p className="text-gray-500">Monitor KPIs, trends, and performance metrics</p>
        </div>
        <div className="flex gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAnalytics}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Orders</p>
                <p className="text-3xl font-bold text-[#001A47]">{summary?.total_orders || 0}</p>
                <div className={`flex items-center gap-1 text-sm ${
                  (summary?.order_growth_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {(summary?.order_growth_percent || 0) >= 0 ? (
                    <TrendingUp className="h-4 w-4" />
                  ) : (
                    <TrendingDown className="h-4 w-4" />
                  )}
                  {formatGrowth(summary?.order_growth_percent || 0)}
                </div>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Fulfillment Rate</p>
                <p className="text-3xl font-bold text-green-600">{summary?.fulfillment_rate || 0}%</p>
                <p className="text-sm text-gray-500">
                  {summary?.orders_completed || 0} completed
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">New Customers</p>
                <p className="text-3xl font-bold text-purple-600">{summary?.new_customers || 0}</p>
                <p className="text-sm text-gray-500">this period</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Complaint Rate</p>
                <p className="text-3xl font-bold text-orange-600">{summary?.complaint_rate || 0}%</p>
                <p className="text-sm text-gray-500">
                  {summary?.complaints || 0} complaints
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <AlertCircle className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Service Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Package className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Deliveries</p>
                <p className="text-2xl font-bold">{summary?.delivery_orders || 0}</p>
              </div>
            </div>
            <div className="mt-4 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-600 rounded-full"
                style={{ 
                  width: `${summary?.total_orders ? (summary.delivery_orders / summary.total_orders * 100) : 0}%` 
                }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <Truck className="h-8 w-8 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Mini-Moves</p>
                <p className="text-2xl font-bold">{summary?.minimove_orders || 0}</p>
              </div>
            </div>
            <div className="mt-4 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-purple-600 rounded-full"
                style={{ 
                  width: `${summary?.total_orders ? (summary.minimove_orders / summary.total_orders * 100) : 0}%` 
                }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Cancelled</p>
                <p className="text-2xl font-bold">{summary?.orders_cancelled || 0}</p>
              </div>
            </div>
            <div className="mt-4 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-red-600 rounded-full"
                style={{ 
                  width: `${summary?.total_orders ? (summary.orders_cancelled / summary.total_orders * 100) : 0}%` 
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Orders Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Daily Orders</CardTitle>
            <CardDescription>Order volume over time</CardDescription>
          </CardHeader>
          <CardContent>
            {dailyData.length > 0 ? (
              <SimpleBarChart data={dailyData} />
            ) : (
              <div className="h-[150px] flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Areas */}
        <Card>
          <CardHeader>
            <CardTitle>Top Areas</CardTitle>
            <CardDescription>Orders by pickup location</CardDescription>
          </CardHeader>
          <CardContent>
            {areaData.length > 0 ? (
              <div className="space-y-3">
                {areaData.slice(0, 6).map((area, index) => (
                  <div key={area.area || `area-${index}`} className="flex items-center gap-3">
                    <div className="w-8 text-center">
                      <span className={`font-bold ${
                        index === 0 ? 'text-yellow-500' :
                        index === 1 ? 'text-gray-400' :
                        index === 2 ? 'text-orange-400' :
                        'text-gray-500'
                      }`}>
                        {index + 1}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium truncate">{area.area}</span>
                        <span className="text-sm text-gray-500">{area.total}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#039fff] rounded-full"
                          style={{ 
                            width: `${(area.total / (areaData[0]?.total || 1)) * 100}%` 
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Referral Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Referral Program Performance</CardTitle>
          <CardDescription>Track referral signups and conversions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-[#fb6402]">{summary?.referral_signups || 0}</p>
              <p className="text-sm text-gray-500">New Referral Codes</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{summary?.referral_uses || 0}</p>
              <p className="text-sm text-gray-500">Codes Used</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-purple-600">
                {summary?.referral_signups ? 
                  Math.round((summary.referral_uses / summary.referral_signups) * 100) || 0 : 0}%
              </p>
              <p className="text-sm text-gray-500">Conversion Rate</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">
                R{(summary?.referral_uses || 0) * 50}
              </p>
              <p className="text-sm text-gray-500">Est. Value Generated</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Period Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Period Comparison</CardTitle>
          <CardDescription>Compare with previous period</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium">Current Period</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Orders</span>
                  <span className="font-bold">{summary?.total_orders || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Completed</span>
                  <span className="font-bold text-green-600">{summary?.orders_completed || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Cancelled</span>
                  <span className="font-bold text-red-600">{summary?.orders_cancelled || 0}</span>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="font-medium">Previous Period</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Orders</span>
                  <span className="font-bold">{summary?.previous_period_orders || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Growth</span>
                  <span className={`font-bold ${
                    (summary?.order_growth_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatGrowth(summary?.order_growth_percent || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsPanel;
